import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TEND_LETTERS, TendTextLogo } from '../ui/TendLogo';
import { detectRegion } from '../../utils/regionDetect';
import '../../landing/landing.css';

/** The public site's shell: the masthead, the page, the closing line and links. */
export function SiteNav({ home = false, signedIn = false }) {
  const to = (hash) => (home ? hash : `/${hash}`);
  return (
    <header className="lp-nav">
      <div className="container">
        <nav className="lp-nav__links" aria-label="Site">
          <a href={to('#how')}>How it works</a>
          <Link to="/try">Try it</Link>
          <Link to="/pricing">Pricing</Link>
        </nav>
        <Link to="/" className="lp-nav__mark" aria-label="Tend home"><TendTextLogo className="h-[22px]" color="currentColor" /></Link>
        <div className="lp-nav__actions">
          {signedIn ? <Link className="lp-btn lp-btn--small" to="/app/today">Open Tend</Link> : (
            <>
              <Link className="lp-link" to="/login">Sign in</Link>
              <Link className="lp-btn lp-btn--small" to="/signup">Start free</Link>
            </>)}
        </div>
      </div>
    </header>
  );
}

/* The wordmark is a small plant you tend. The letters lean toward your finger and spring back; sweep
   across all four and the sprout grows one step, once a day. It is counted in days, never a streak:
   miss a day and it just waits. Saved in this browser only. */
const SPROUT = 'tend.sprout';
const localDay = () => new Date().toLocaleDateString('en-CA');
const readSprout = () => { try { return JSON.parse(localStorage.getItem(SPROUT)) || { days: 0, last: '' }; } catch { return { days: 0, last: '' }; } };
const REACH = 60;   // how far a finger pulls, in wordmark units
const STEM_X = 143; const STEM_Y = 40;   // the top of the n

function FootMark() {
  const [sprout, setSprout] = useState(readSprout);
  const [on, setOn] = useState({});
  const svg = useRef(null);
  const plant = useRef(null);
  const live = useRef({ active: false, px: 0, raf: 0, parts: [] });
  const tended = sprout.last === localDay();
  const stage = Math.min(sprout.days, 6);
  const height = 6 + stage * 7;

  useEffect(() => {
    if (tended || !TEND_LETTERS.every(([k]) => on[k])) return;
    const next = { days: sprout.days + 1, last: localDay() };
    try { localStorage.setItem(SPROUT, JSON.stringify(next)); } catch { /* private mode: it grows for this visit */ }
    setSprout(next);
  }, [on, tended, sprout.days]);
  useEffect(() => () => cancelAnimationFrame(live.current.raf), []);

  const frame = () => {
    const l = live.current;
    let moving = l.active;
    l.parts.forEach((p) => {
      const d = (l.px - p.cx) / REACH;
      const pull = l.active ? Math.exp(-d * d) : 0;
      const lean = Math.max(-1, Math.min(1, d)) * pull * p.lean;
      p.v = (p.v + (lean - p.a) * 0.12) * 0.78; p.a += p.v;
      p.sv = (p.sv + (pull * p.stretch - p.s) * 0.12) * 0.78; p.s += p.sv;
      p.el.style.transform = p.plant ? `rotate(${(-p.a).toFixed(2)}deg)` : `skewX(${p.a.toFixed(2)}deg) scaleY(${(1 + p.s).toFixed(3)})`;
      if (Math.abs(p.a) > 0.02 || Math.abs(p.v) > 0.02 || Math.abs(p.s) > 0.001) moving = true;
    });
    l.raf = moving ? requestAnimationFrame(frame) : 0;
  };
  const wake = (e) => {
    const l = live.current;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!l.parts.length) {
      const part = (el, cx, lean, stretch, isPlant) => ({ el, cx, lean, stretch, plant: isPlant, a: 0, v: 0, s: 0, sv: 0 });
      l.parts = [...svg.current.querySelectorAll('[data-letter]')].map((el) => { const b = el.getBBox(); return part(el, b.x + b.width / 2, -12, 0.1, false); });
      if (plant.current) l.parts.push(part(plant.current, STEM_X, 12, 0, true));
    }
    const r = svg.current.getBoundingClientRect();
    l.px = ((e.clientX - r.left) / r.width) * 226;
    l.active = true;
    if (!l.raf) l.raf = requestAnimationFrame(frame);
  };
  const rest = () => { live.current.active = false; };
  const touch = (e) => {
    wake(e);
    const k = document.elementFromPoint(e.clientX, e.clientY)?.dataset?.letter;
    if (k) setOn((s) => (s[k] ? s : { ...s, [k]: true }));
  };
  useEffect(() => { live.current.parts = []; }, [stage]);   // a new leaf: re-measure on the next touch

  return (
    <div className="lp-foot__play" style={{ '--grow': stage }}>
      <svg ref={svg} className="lp-foot__mark" viewBox="0 0 226 106" fill="none" role="img" aria-label="Tend"
        onPointerDown={touch} onPointerMove={touch} onPointerLeave={rest} onPointerUp={rest} onPointerCancel={rest}>
        {stage > 0 && (
          <g ref={plant} className="lp-sprout">
            <path className="lp-sprout__stem" key={stage} pathLength="1" d={`M${STEM_X} ${STEM_Y}C${STEM_X - 2} ${STEM_Y - height * 0.4} ${STEM_X + 3} ${STEM_Y - height * 0.7} ${STEM_X} ${STEM_Y - height}`} />
            {Array.from({ length: stage }, (_, i) => (
              <g key={i} transform={`translate(${STEM_X} ${STEM_Y - (height * (i + 1)) / stage}) scale(${i % 2 ? -1 : 1} 1)`}>
                <path className="lp-sprout__leaf" d="M0 0C5-8 14-10 22-7C18 2 8 5 0 0Z" />
              </g>))}
          </g>)}
        {TEND_LETTERS.map(([k, d]) => <path key={k} d={d} data-letter={k} className={tended || on[k] ? 'is-on' : undefined} />)}
      </svg>
      <p className="lp-foot__hint" role="status">
        {!tended ? (sprout.days ? `${sprout.days} ${sprout.days === 1 ? 'day' : 'days'} tended. Sweep the letters to help it grow` : 'Sweep across the letters to plant something')
          : <>{sprout.days === 1 ? 'Planted. It grows each day you come back.' : `${sprout.days} days tended. Miss a day and it just waits.`} <Link to="/signup">Start a real habit</Link></>}
      </p>
    </div>
  );
}

export function SiteFooter({ credits }) {
  const home = (hash) => `/${hash}`;
  return (
    <footer className="lp-foot">
      <div className="container">
        <div className="lp-foot__top">
          <p className="lp-foot__line">Tend keeps the day light.</p>
          <nav className="lp-foot__cols" aria-label="Footer">
            <div><h3>Product</h3><a href={home('#how')}>How it works</a><Link to="/try">Try it</Link><Link to="/apps">Apps</Link><Link to="/pricing">Pricing</Link></div>
            <div><h3>Learn</h3><Link to="/whats-new">What's new</Link><Link to="/trust">Your data</Link><a href={home('#questions')}>Questions</a></div>
            <div><h3>Company</h3><Link to="/contact">Contact</Link><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
          </nav>
        </div>
        <FootMark />
        <div className="lp-foot__row">
          <span>© 2026 Tend</span>
          {credits && <p className="lp-foot__credits">{credits}</p>}
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Back to top</a>
        </div>
      </div>
    </footer>
  );
}

const FONTS = 'https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400&family=Funnel+Sans:wght@400;500&display=swap';

export function SitePage({ children, home = false, signedIn = false }) {
  return (
    <div className="lp">
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href={FONTS} rel="stylesheet" />
      </Helmet>
      <SiteNav home={home} signedIn={signedIn} />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}

const ROWS = [
  ['Habits', 'Unlimited', 'Unlimited'],
  ['Today, crews and Ask Tend', 'Included', 'Included'],
  ['Helpers running at once', '1', '10'],
  ['Helper runs a day', '3', '24'],
  ['How often a helper checks', 'Once a day', 'Every 4 hours'],
];

/**
 * Free and Pro as one table. Without onChoose the actions link to sign up (the landing page);
 * with it they call it (the pricing page, which starts checkout).
 */
export function PricingTable({ onChoose, currentPlan, busy, headingLevel = 'h2' }) {
  const [r, setR] = useState({ symbol: '₹', price: '99', isIndia: true });
  const [yearly, setYearly] = useState(null);     // the yearly price, once it is switched on
  const [cycle, setCycle] = useState('monthly');
  useEffect(() => { setR(detectRegion()); }, []);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/subscription/pricing`).then((x) => (x.ok ? x.json() : {})).then((d) => setYearly(d.yearly_inr || null)).catch(() => {});
  }, []);
  const annual = yearly && r.isIndia && cycle === 'yearly';
  const cost = annual ? String(yearly) : r.price;
  const per = annual ? ' a year' : ' a month';
  const H = headingLevel;
  const action = (plan, label, clear) => {
    const cls = `lp-btn${clear ? ' lp-btn--clear' : ''}`;
    if (!onChoose) return <Link className={cls} to="/signup">{label}</Link>;
    const current = currentPlan === plan;
    return (
      <button type="button" className={cls} onClick={() => onChoose(plan, cycle)} aria-disabled={busy || undefined} disabled={busy}>
        {current ? 'Your plan' : busy && plan === 'pro' ? 'Opening checkout' : label}
      </button>);
  };
  return (
    <section className="lp-section" id="pricing" aria-labelledby="pricing-h">
      <div className="container">
        <div className="lp-head">
          <H id="pricing-h" className="lp-h2">Free for habits and crews, Pro for more helpers</H>
          <p>Your first helper comes with 7 days of 3 helpers, free. Pro runs up to 10, checking as often as every 4 hours.</p>
        </div>
        {yearly && r.isIndia && (
          <div className="lp-cycle" role="group" aria-label="Billing">
            {[['monthly', 'Monthly'], ['yearly', `Yearly, ${r.symbol}${yearly}`]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={cycle === k} onClick={() => setCycle(k)}>{label}</button>))}
          </div>)}
        <table className="lp-price">
          <thead><tr><td /><th scope="col">Free</th><th scope="col">Pro</th></tr></thead>
          <tbody>
            <tr>
              <th scope="row">Price</th>
              <td className="lp-price__cost num">{r.symbol}0</td>
              <td className="lp-price__cost num">{r.symbol}{cost} <small>{per.trim()}</small></td>
            </tr>
            {ROWS.map(([k, a, b]) => <tr key={k}><th scope="row">{k}</th><td className="num">{a}</td><td className="num">{b}</td></tr>)}
          </tbody>
          <tfoot><tr><td /><td>{action('free', 'Start free', true)}</td><td>{action('pro', 'Get Pro', false)}</td></tr></tfoot>
        </table>
        <div className="lp-price-cards">
          {[['Free', `${r.symbol}0`, 1, action('free', 'Start free', true)], ['Pro', `${r.symbol}${cost}`, 2, action('pro', 'Get Pro', false)]].map(([name, cost, col, btn]) => (
            <div key={name} className="lp-price-card">
              <p className="lp-price-card__name">{name}</p>
              <p className="lp-price__cost num">{cost}{col === 2 && <small>{per}</small>}</p>
              <dl>{ROWS.map((row) => <div key={row[0]}><dt>{row[0]}</dt><dd className="num">{row[col]}</dd></div>)}</dl>
              {btn}
            </div>))}
        </div>
        {!r.isIndia && <p className="lp-price__note">In India, Pro is ₹99 a month.</p>}
      </div>
    </section>
  );
}
